import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantMemberApplicationAPI } from '../../api/tenantMemberApplication';
import { tenantAPI } from '../../api/tenant';
import { fetchAuthContext, refreshAuthSession } from '../../api/auth';
import AlertModal from '../../components/AlertModal';
import { APP_LOGO, APP_LOGO_ALT } from '../../config/brand';

const STATUS_LABELS = {
  PENDING: '待审核',
  APPROVED: '已通过',
  REJECTED: '已拒绝',
  CANCELLED: '已撤销',
};

function statusLabel(status) {
  return STATUS_LABELS[status] || status || '-';
}

export default function TenantOnboardingPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('join');
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [tenantCode, setTenantCode] = useState('');
  const [applyReason, setApplyReason] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '', title: '提示' });

  const showAlert = (message, title = '提示') => {
    setAlertModal({ isOpen: true, message, title });
  };

  const refreshContext = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchAuthContext({ includeStats: false, backgroundStats: false });
      setCtx(next);
      if (next && !next.needsTenantJoin && !next.pendingTenantApplication && !next.pendingTenantProvision) {
        try {
          await refreshAuthSession();
        } catch (e) {
          console.warn('[onboarding] refresh session failed', e);
        }
        navigate('/welcome', { replace: true });
      }
      return next;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const loadTenants = useCallback(async () => {
    try {
      const res = await tenantMemberApplicationAPI.getJoinOptions();
      const list = res?.data?.data ?? res?.data ?? [];
      setTenants(Array.isArray(list) ? list : []);
    } catch (e) {
      setTenants([]);
    }
  }, []);

  useEffect(() => {
    refreshContext();
    loadTenants();
  }, [refreshContext, loadTenants]);

  const allowSelfProvision = ctx?.allowSelfProvision !== false && ctx?.deployment?.allowSelfProvision !== false;
  const pendingMember = ctx?.pendingTenantApplication || null;
  const pendingProvision = ctx?.pendingTenantProvision || null;

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    if (pendingMember || pendingProvision) {
      showAlert('您已有待审核申请，请等待审批或先撤销');
      return;
    }
    const code = tenantCode.trim();
    if (!code && tenants.length === 0) {
      showAlert('请输入租户编码');
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = code
        ? { tenantCode: code, applyReason: applyReason.trim() || undefined }
        : { tenantId: tenants[0]?.id, applyReason: applyReason.trim() || undefined };
      if (!payload.tenantId && !payload.tenantCode) {
        showAlert('请输入租户编码');
        return;
      }
      await tenantMemberApplicationAPI.submit(payload);
      showAlert('申请已提交，请等待租户管理员审批', '提交成功');
      await refreshContext();
    } catch (err) {
      showAlert(err?.response?.data?.message || err.message || '提交失败', '错误');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleProvisionSubmit = async (e) => {
    e.preventDefault();
    if (!allowSelfProvision) {
      showAlert('当前环境未开放自助开通');
      return;
    }
    if (pendingMember || pendingProvision) {
      showAlert('您已有待审核申请');
      return;
    }
    const name = tenantName.trim();
    if (!name) {
      showAlert('请输入组织名称');
      return;
    }
    setSubmitLoading(true);
    try {
      await tenantAPI.submitProvisionApplication({
        tenantName: name,
        organizationName: organizationName.trim() || undefined,
        contactName: contactName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
      });
      showAlert('已提交开通申请，请等待平台审核', '提交成功');
      await refreshContext();
    } catch (err) {
      showAlert(err?.response?.data?.message || err.message || '提交失败', '错误');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancelMember = async () => {
    if (!pendingMember?.id) return;
    setSubmitLoading(true);
    try {
      await tenantMemberApplicationAPI.cancel(pendingMember.id);
      showAlert('申请已撤销');
      await refreshContext();
    } catch (err) {
      showAlert(err?.response?.data?.message || err.message || '撤销失败', '错误');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancelProvision = async () => {
    if (!pendingProvision?.id) return;
    setSubmitLoading(true);
    try {
      await tenantAPI.cancelProvisionApplication(pendingProvision.id);
      showAlert('申请已撤销');
      await refreshContext();
    } catch (err) {
      showAlert(err?.response?.data?.message || err.message || '撤销失败', '错误');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEnterApp = async () => {
    setSubmitLoading(true);
    try {
      await refreshAuthSession();
      await refreshContext();
      navigate('/welcome', { replace: true });
    } catch (err) {
      showAlert('会话同步失败，请退出后重新登录', '提示');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10 bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950">
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-8">
        <div className="mb-6 text-center">
          <img src={APP_LOGO} alt={APP_LOGO_ALT} className="mx-auto h-12 w-12 rounded-xl object-cover" />
          <h1 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">加入或创建组织</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            完成组织绑定后可使用生产、商城等业务功能
          </p>
        </div>

        <div className="mb-6 flex rounded-xl bg-gray-100 p-1 dark:bg-gray-900">
          <button
            type="button"
            onClick={() => setTab('join')}
            className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${
              tab === 'join' ? 'bg-white text-blue-600 shadow dark:bg-gray-800 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            加入组织
          </button>
          {allowSelfProvision && (
            <button
              type="button"
              onClick={() => setTab('create')}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${
                tab === 'create' ? 'bg-white text-blue-600 shadow dark:bg-gray-800 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              创建组织
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-500">加载中...</div>
        ) : pendingMember ? (
          <div className="space-y-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 dark:border-blue-900 dark:bg-blue-950/30">
            <div className="text-sm font-medium text-gray-900 dark:text-white">加入申请 · {statusLabel(pendingMember.status)}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">目标：{pendingMember.tenantName || pendingMember.tenantCode || '-'}</div>
            {pendingMember.reviewComment && (
              <div className="text-sm text-gray-500">意见：{pendingMember.reviewComment}</div>
            )}
            {pendingMember.status === 'PENDING' ? (
              <button
                type="button"
                disabled={submitLoading}
                onClick={handleCancelMember}
                className="w-full rounded-lg border border-gray-300 py-2 text-sm text-gray-700 hover:bg-white dark:border-gray-600 dark:text-gray-200"
              >
                撤销申请
              </button>
            ) : (
              <button
                type="button"
                disabled={submitLoading}
                onClick={handleEnterApp}
                className="w-full rounded-lg bg-blue-600 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                进入系统
              </button>
            )}
            <button type="button" onClick={refreshContext} className="w-full text-xs text-blue-600 hover:underline">
              刷新状态
            </button>
          </div>
        ) : pendingProvision ? (
          <div className="space-y-4 rounded-xl border border-emerald-100 bg-emerald-50/60 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
            <div className="text-sm font-medium text-gray-900 dark:text-white">开通申请 · {statusLabel(pendingProvision.status)}</div>
            <div className="text-sm text-gray-600 dark:text-gray-300">组织：{pendingProvision.tenantName || '-'}</div>
            {pendingProvision.reviewComment && (
              <div className="text-sm text-gray-500">意见：{pendingProvision.reviewComment}</div>
            )}
            {pendingProvision.status === 'PENDING' ? (
              <button
                type="button"
                disabled={submitLoading}
                onClick={handleCancelProvision}
                className="w-full rounded-lg border border-gray-300 py-2 text-sm text-gray-700 hover:bg-white dark:border-gray-600 dark:text-gray-200"
              >
                撤销申请
              </button>
            ) : (
              <button
                type="button"
                disabled={submitLoading}
                onClick={handleEnterApp}
                className="w-full rounded-lg bg-blue-600 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                进入系统
              </button>
            )}
            <button type="button" onClick={refreshContext} className="w-full text-xs text-blue-600 hover:underline">
              刷新状态
            </button>
          </div>
        ) : tab === 'join' ? (
          <form className="space-y-4" onSubmit={handleJoinSubmit}>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">租户编码</label>
              <input
                type="text"
                value={tenantCode}
                onChange={(e) => setTenantCode(e.target.value)}
                placeholder="向组织管理员索取编码"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">申请说明（选填）</label>
              <textarea
                value={applyReason}
                onChange={(e) => setApplyReason(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <button
              type="submit"
              disabled={submitLoading}
              className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitLoading ? '提交中...' : '提交加入申请'}
            </button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handleProvisionSubmit}>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">组织名称</label>
              <input
                type="text"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">默认组织名称（选填）</label>
              <input
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="联系人"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <input
                type="text"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="联系电话"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <button
              type="submit"
              disabled={submitLoading}
              className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitLoading ? '提交中...' : '提交开通申请'}
            </button>
          </form>
        )}
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
}
